import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getMerchantStats,
  getPartnerStats,
  getCompletedOrderStats,
  getCustomerStats,
  MerchantStats,
  PartnerStats,
  CompletedOrderStats,
  CustomerStats,
  getMerchantKycStatusStats, MerchantKycStatusStats,
  MerchantByLenderStats, getMerchantByLenderStats,
  OrderStatusStats, getOrderStatusStats,
  PreApprovalStats, getPreApprovalStats 
} from '../../services/dashboard';

export interface DashboardState {
  merchantKycStats: MerchantKycStatusStats | null;
  loadingKycStats: boolean;
  hasFetchedKycStats: boolean;
  merchantStats: MerchantStats | null;
  partnerStats: PartnerStats | null;
  orderStats: CompletedOrderStats | null;
  customerStats: CustomerStats | null;
  loading: boolean;
  hasFetched: boolean;
  merchantByLenderStats: MerchantByLenderStats | null;
  loadingMerchantByLender: boolean;
  hasFetchedMerchantByLender: boolean;
  orderStatusStats: OrderStatusStats | null;
  loadingOrderStats: boolean;
  hasFetchedOrderStats: boolean;
  preApprovalStats: PreApprovalStats | null;
  loadingPreApprovalStats: boolean;
  hasFetchedPreApprovalStats: boolean;
}

const initialState: DashboardState = {
  merchantKycStats: null,
  loadingKycStats: false,
  hasFetchedKycStats: false,
  merchantStats: null,
  partnerStats: null,
  orderStats: null,
  customerStats: null,
  loading: false,
  hasFetched: false,
  merchantByLenderStats: null,
  loadingMerchantByLender: false,
  hasFetchedMerchantByLender: false,
  orderStatusStats: null,
  loadingOrderStats: false,
  hasFetchedOrderStats: false,
  preApprovalStats: null,
  loadingPreApprovalStats: false,
  hasFetchedPreApprovalStats: false,
};

// Fetch all stats once
export const fetchDashboardStats = createAsyncThunk(
  'dashboard/fetchDashboardStats',
  async () => {
    const [merchants, partners, orders, customers] = await Promise.all([
      getMerchantStats(),
      getPartnerStats(),
      getCompletedOrderStats(),
      getCustomerStats()
    ]);
    return { merchants, partners, orders, customers };
  }
);

// Fetch KYC status once
export const fetchMerchantKycStats = createAsyncThunk(
  'dashboard/fetchMerchantKycStats',
  async () => {
    const data = await getMerchantKycStatusStats();
    return data;
  }
);

export const fetchMerchantByLenderStats = createAsyncThunk(
  'dashboard/fetchMerchantByLenderStats',
  async () => {
    const data = await getMerchantByLenderStats();
    return data;
  }
);

export const fetchOrderStatusStats = createAsyncThunk(
  'dashboard/fetchOrderStatusStats',
  async () => {
    const data = await getOrderStatusStats();
    return data;
  }
);

export const fetchPreApprovalStats = createAsyncThunk(
  'dashboard/fetchPreApprovalStats',
  async () => {
    const data = await getPreApprovalStats();
    return data;
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchDashboardStats.pending, state => {
      state.loading = true;
    });
    builder.addCase(fetchDashboardStats.fulfilled, (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.hasFetched = true;
      state.merchantStats = action.payload.merchants;
      state.partnerStats = action.payload.partners;
      state.orderStats = action.payload.orders;
      state.customerStats = action.payload.customers;
    });
    builder.addCase(fetchDashboardStats.rejected, state => {
      state.loading = false;
      state.hasFetched = false;
    });
    builder.addCase(fetchMerchantKycStats.pending, state => {
      state.loadingKycStats = true;
    });
    builder.addCase(fetchMerchantKycStats.fulfilled, (state, action: PayloadAction<MerchantKycStatusStats>) => {
      state.loadingKycStats = false;
      state.hasFetchedKycStats = true;
      state.merchantKycStats = action.payload;
    });
    builder.addCase(fetchMerchantKycStats.rejected, state => {
      state.loadingKycStats = false;
      state.hasFetchedKycStats = false;
    });
    builder.addCase(fetchMerchantByLenderStats.pending, state => {
      state.loadingMerchantByLender = true;
    });
    builder.addCase(fetchMerchantByLenderStats.fulfilled, (state, action: PayloadAction<MerchantByLenderStats>) => {
      state.loadingMerchantByLender = false;
      state.hasFetchedMerchantByLender = true;
      state.merchantByLenderStats = action.payload;
    });
    builder.addCase(fetchMerchantByLenderStats.rejected, state => {
      state.loadingMerchantByLender = false;
      state.hasFetchedMerchantByLender = false;
    });
    builder.addCase(fetchOrderStatusStats.pending, state => {
      state.loadingOrderStats = true;
    });
    builder.addCase(fetchOrderStatusStats.fulfilled, (state, action: PayloadAction<OrderStatusStats>) => {
      state.loadingOrderStats = false;
      state.hasFetchedOrderStats = true;
      state.orderStatusStats = action.payload;
    });
    builder.addCase(fetchOrderStatusStats.rejected, state => {
      state.loadingOrderStats = false;
      state.hasFetchedOrderStats = false;
    });
    builder.addCase(fetchPreApprovalStats.pending, state => {
      state.loadingPreApprovalStats = true;
    });
    builder.addCase(fetchPreApprovalStats.fulfilled, (state, action: PayloadAction<PreApprovalStats>) => {
      state.loadingPreApprovalStats = false;
      state.hasFetchedPreApprovalStats = true;
      state.preApprovalStats = action.payload;
    });
    builder.addCase(fetchPreApprovalStats.rejected, state => {
      state.loadingPreApprovalStats = false;
      state.hasFetchedPreApprovalStats = false;
    });
  }
});

export default dashboardSlice.reducer;
